import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaQuinceM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // compute left/right widths early so title fits inside the left column
      const usableW = Math.max(0, width - padding * 2);
      const interGap = 8; // gap between left block and right text column
      const leftAreaW = Math.round(usableW * 0.75);
      const rightAreaW = Math.max(0, usableW - leftAreaW - interGap);
      const leftAreaX = padding;
      const rightAreaX = leftAreaX + leftAreaW + interGap;

      // draw left (gray) and right (white) column backgrounds before the title
      ctx.fillStyle = opts.leftAreaBg || '#f3f4f6';
      ctx.fillRect(leftAreaX, 0, leftAreaW, height);

      ctx.fillStyle = opts.rightAreaBg || '#ffffff';
      ctx.fillRect(rightAreaX, 0, rightAreaW, height);

      // title square + title (placed inside the left column with a small inner inset)
      const titleFontSize = opts.titleFontSize || 40;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 18;
      const squareText = opts.titleSquareText || '2.3';
      const innerLeftPad = 8;
      const squareX = leftAreaX + innerLeftPad;

      // square (inside left column)
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified) uses full canvas width
      const title = opts.title || props.title || '';
      const titleX = squareX + squareSize + squareGap;
      const titleMaxW = Math.min(
        typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 1100,
        Math.max(0, width - titleX - padding)
      );

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let yPos = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, yPos);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, yPos);
          }
          yPos += lineH;
        }
        return yPos;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      let chartsStartY;
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        chartsStartY = afterSubY + 10;
      } else {
        chartsStartY = afterTitleY + 10;
      }

      // draw two charts inside left column by explicitly splitting the left column into two separated sides
      const graficaUnoData = props.graficaUnoData || [];
      let graficaDosData = props.graficaDosData || [];

      // split left column into two separated sides (leftSide and rightSide)
      const subGap = 12;
      const leftInnerPad = 8;
      const subUsable = Math.max(0, leftAreaW - leftInnerPad * 2);
      const splitW = Math.floor((subUsable - subGap) / 2);
      const leftSideX = leftAreaX + leftInnerPad;
      const rightSideX = leftSideX + splitW + subGap;

      // draw a subtle divider between the two sub-sides
      const dividerX = leftSideX + splitW + Math.round(subGap / 2) - 1;
      ctx.fillStyle = opts.leftSplitDivider || '#e5e7eb';
      const dividerTop = chartsStartY - 8;
      const dividerHeight = Math.max(0, height - dividerTop - padding);
      ctx.fillRect(dividerX, dividerTop, 2, dividerHeight);

      // We'll align both graphics from the same baseline (graphicsBottom).
      // Compute the shared bottom for graphics.
      const computedAreaBottom = Math.max(chartsStartY, height - padding - 400);

      // -----------------------------
      // graficaUno (pie) - left sub-side, bottom-aligned with shared baseline
      // -----------------------------
      const maxPieByWidth = Math.max(0, splitW - 16);
      const maxPieByHeight = Math.max(0, computedAreaBottom - chartsStartY - 8);
      const pieDiameterLeft = Math.max(0, Math.min(600, maxPieByWidth, maxPieByHeight));
      const pieRLeft = Math.max(10, Math.round(pieDiameterLeft / 2));
      const pieXLeft = leftSideX + Math.round(splitW / 2);
      const pieYLeft = Math.max(chartsStartY + pieRLeft, computedAreaBottom - pieRLeft);

      const totalValLeft = graficaUnoData.reduce((s, it) => s + (Number(it.cantidad) || 0), 0) || 1;
      let angLeft = -Math.PI / 2;
      const palette = opts.palette || ['#ef4444', '#06b6d4', '#f59e0b', '#10b981', '#6366f1', '#a78bfa'];
      const contrastFor = (hex) => {
        try {
          const h = hex.replace('#','');
          const r = parseInt(h.length===3 ? h[0]+h[0] : h.slice(0,2),16);
          const g = parseInt(h.length===3 ? h[1]+h[1] : h.slice(2,4),16);
          const b = parseInt(h.length===3 ? h[2]+h[2] : h.slice(4,6),16);
          const lum = 0.299*r + 0.587*g + 0.114*b;
          return lum < 140 ? '#ffffff' : '#0f172a';
        } catch (e) { return '#ffffff'; }
      };
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        const v = Number(it.cantidad) || 0;
        const a = (v / totalValLeft) * Math.PI * 2;
        const startAng = angLeft;
        const endAng = angLeft + a;

        ctx.beginPath();
        ctx.moveTo(pieXLeft, pieYLeft);
        const fillColor = palette[i % palette.length];
        ctx.fillStyle = fillColor;
        ctx.arc(pieXLeft, pieYLeft, pieRLeft, startAng, endAng);
        ctx.lineTo(pieXLeft, pieYLeft);
        ctx.fill();

        const mid = startAng + a / 2;
        const labelRadius = Math.max(12, Math.round(pieRLeft * 0.62));
        const lx = pieXLeft + Math.cos(mid) * labelRadius;
        const ly = pieYLeft + Math.sin(mid) * labelRadius;
        const pct = Math.round((v / totalValLeft) * 1000) / 10;
        const labelText = (Number.isFinite(v) ? String(v) : '') + (typeof v === 'number' ? ` (${pct}%)` : '');

        ctx.save();
        ctx.font = `${Math.max(10, Math.round(pieRLeft * 0.12))}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = contrastFor(fillColor);
        
        const labelWidth = ctx.measureText(labelText).width;
        const maxLabelWidth = Math.max(20, Math.round(labelRadius * a * 0.9));
        const canFitInside = a > 0.12 && labelWidth <= maxLabelWidth;
        
        if (canFitInside) {
          // Label fits inside slice
          ctx.fillText(labelText, lx, ly);
        } else {
          // Label doesn't fit - place outside with leader line
          const outsideR = pieRLeft + 14;
          const ox = pieXLeft + Math.cos(mid) * outsideR;
          const oy = pieYLeft + Math.sin(mid) * outsideR;
          const alignRight = Math.cos(mid) < 0;
          ctx.textAlign = alignRight ? 'right' : 'left';
          ctx.fillStyle = contrastFor(fillColor);
          ctx.strokeStyle = opts.pieOutsideLineColor || 'rgba(15,23,42,0.35)';
          ctx.lineWidth = 1;
          const edgeX = pieXLeft + Math.cos(mid) * (pieRLeft - 1);
          const edgeY = pieYLeft + Math.sin(mid) * (pieRLeft - 1);
          const elbowX = pieXLeft + Math.cos(mid) * (pieRLeft + 6);
          const elbowY = pieYLeft + Math.sin(mid) * (pieRLeft + 6);
          ctx.beginPath();
          ctx.moveTo(edgeX, edgeY);
          ctx.lineTo(elbowX, elbowY);
          ctx.lineTo(ox, oy);
          ctx.stroke();
          ctx.fillText(labelText, ox + (alignRight ? -4 : 4), oy);
        }
        ctx.restore();

        angLeft += a;
      }

      // left legend is placed below shared baseline
      ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let legendYLeft = computedAreaBottom + 8;
      const legendXLeft = leftSideX + 8;
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        ctx.fillStyle = palette[i % palette.length];
        ctx.fillRect(legendXLeft, legendYLeft, 12, 12);
        ctx.fillStyle = opts.legendColor || '#111827';
        ctx.fillText(`${it.orientacion} (${it.cantidad})`, legendXLeft + 18, legendYLeft - 2);
        legendYLeft += 18;
      }

      // -----------------------------
      // graficaDos (pie) - right sub-side, bottom-aligned with shared baseline
      // -----------------------------
      const rightPieData = graficaDosData;
      const totalValRight = rightPieData.reduce((s, it) => s + (Number(it.cantidad) || 0), 0) || 1;

      const maxPieByWidthR = Math.max(0, splitW - 16);
      const maxPieByHeightR = Math.max(0, computedAreaBottom - chartsStartY - 8);
      const pieDiameterR = Math.max(0, Math.min(600, maxPieByWidthR, maxPieByHeightR));
      const pieRR = Math.max(10, Math.round(pieDiameterR / 2));
      const pieXR = rightSideX + Math.round(splitW / 2);
      const pieYR = Math.max(chartsStartY + pieRR, computedAreaBottom - pieRR);

      let angR = -Math.PI / 2;
      for (let i = 0; i < rightPieData.length; i++) {
        const it = rightPieData[i];
        const v = Number(it.cantidad) || 0;
        const a = (v / totalValRight) * Math.PI * 2;
        const startAng = angR;
        const endAng = angR + a;

        ctx.beginPath();
        ctx.moveTo(pieXR, pieYR);
        const fillColor = palette[(i + 1) % palette.length]; // offset color from left
        ctx.fillStyle = fillColor;
        ctx.arc(pieXR, pieYR, pieRR, startAng, endAng);
        ctx.lineTo(pieXR, pieYR);
        ctx.fill();

        const mid = startAng + a / 2;
        const labelRadius = Math.max(12, Math.round(pieRR * 0.62));
        const lx = pieXR + Math.cos(mid) * labelRadius;
        const ly = pieYR + Math.sin(mid) * labelRadius;
        const pct = Math.round((v / totalValRight) * 1000) / 10;
        const labelText = (Number.isFinite(v) ? String(v) : '') + (typeof v === 'number' ? ` (${pct}%)` : '');

        ctx.save();
        ctx.font = `${Math.max(10, Math.round(pieRR * 0.12))}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = contrastFor(fillColor);
        
        const labelWidth = ctx.measureText(labelText).width;
        const maxLabelWidth = Math.max(20, Math.round(labelRadius * a * 0.9));
        const canFitInside = a > 0.12 && labelWidth <= maxLabelWidth;
        
        if (canFitInside) {
          // Label fits inside slice
          ctx.fillText(labelText, lx, ly);
        } else {
          // Label doesn't fit - place outside with leader line
          const outsideR = pieRR + 14;
          const ox = pieXR + Math.cos(mid) * outsideR;
          const oy = pieYR + Math.sin(mid) * outsideR;
          const alignRight = Math.cos(mid) < 0;
          ctx.textAlign = alignRight ? 'right' : 'left';
          ctx.fillStyle = contrastFor(fillColor);
          ctx.strokeStyle = opts.pieOutsideLineColor || 'rgba(15,23,42,0.35)';
          ctx.lineWidth = 1;
          const edgeX = pieXR + Math.cos(mid) * (pieRR - 1);
          const edgeY = pieYR + Math.sin(mid) * (pieRR - 1);
          const elbowX = pieXR + Math.cos(mid) * (pieRR + 6);
          const elbowY = pieYR + Math.sin(mid) * (pieRR + 6);
          ctx.beginPath();
          ctx.moveTo(edgeX, edgeY);
          ctx.lineTo(elbowX, elbowY);
          ctx.lineTo(ox, oy);
          ctx.stroke();
          ctx.fillText(labelText, ox + (alignRight ? -4 : 4), oy);
        }
        ctx.restore();

        angR += a;
      }

      // right legend below shared baseline
      ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let legendYRight = computedAreaBottom + 8;
      const legendXRight = rightSideX + 8;
      for (let i = 0; i < rightPieData.length; i++) {
        const it = rightPieData[i];
        ctx.fillStyle = palette[(i + 1) % palette.length];
        ctx.fillRect(legendXRight, legendYRight, 12, 12);
        ctx.fillStyle = opts.legendColor || '#111827';
        ctx.fillText(`${it.identidad} (${it.cantidad})`, legendXRight + 18, legendYRight - 2);
        legendYRight += 18;
      }

      // compute left/right bottom text positions (directly under each legend block)
      const leftTextX = leftSideX + 8;
      const leftTextMaxW = splitW - 16;
      const leftTextY = legendYLeft + 6;
      const rightTextX = rightSideX + 8;
      const rightTextMaxW = splitW - 16;
      const rightTextY = legendYRight + 6;

      // bottom texts: place textoUno under left sub-side and textoDos under right sub-side, then footer
      const textoUno = opts.textoUno || props.textoUno || '';
      const textoDos = opts.textoDos || props.textoDos || '';
      const footerText = opts.footerText || props.footerText || '';
      ctx.font = `${opts.bottomFontSize || 16}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      if (textoUno) drawSmallWrap(ctx, textoUno, leftTextX, leftTextY, leftTextMaxW, 20);
      if (textoDos) drawSmallWrap(ctx, textoDos, rightTextX, rightTextY, rightTextMaxW, 20);

      // right column textoRight (array supported)
      const textoRightRaw = opts.textoRight ?? opts.rightText ?? props.textoRight ?? props.rightText ?? '';
      const rightParagraphs = [];
      if (Array.isArray(textoRightRaw)) {
        for (let p of textoRightRaw) if (String(p || '').trim()) rightParagraphs.push(String(p));
      } else if (String(textoRightRaw || '').trim()) {
        rightParagraphs.push(String(textoRightRaw));
      }

      if (rightParagraphs.length) {
        ctx.save();

        const wrapLines = (c, text, maxW) => {
          const words = String(text).split(/\s+/).filter(Boolean);
          const lines = [];
          let cur = '';
          for (let w of words) {
            const test = cur ? cur + ' ' + w : w;
            if (c.measureText(test).width <= maxW) {
              cur = test;
            } else {
              if (cur) lines.push(cur);
              cur = w;
            }
          }
          if (cur) lines.push(cur);
          return lines;
        };

        let rightFontSize = typeof opts.textoRightSize === 'number' ? opts.textoRightSize : 35;
        const minFontSize = 8;
        ctx.fillStyle = opts.textoRightColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        const maxRightW = Math.max(8, rightAreaW - 24);
        const topBound = padding;
        const bottomBound = height - padding;

        let allLines = [];
        let lineH = 0;
        let totalH = 0;
        while (rightFontSize >= minFontSize) {
          ctx.font = `${rightFontSize}px Inter, Arial, sans-serif`;
          allLines = [];
          const spaceW = ctx.measureText(' ').width;
          for (let p of rightParagraphs) {
            const words = String(p).split(/\s+/).filter(Boolean);
            const lines = [];
            let cur = [];
            let curW = 0;
            for (let w of words) {
              const ww = ctx.measureText(w).width;
              if (cur.length === 0) {
                cur.push(w);
                curW = ww;
              } else if (curW + spaceW + ww <= maxRightW) {
                cur.push(w);
                curW += spaceW + ww;
              } else {
                lines.push({ words: cur.slice(), wordsWidth: curW });
                cur = [w];
                curW = ww;
              }
            }
            if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });
            allLines.push(...lines, { words: [], wordsWidth: 0 }); // blank line between paragraphs
          }
          if (allLines.length && allLines[allLines.length - 1].words.length === 0) allLines.pop();
          lineH = Math.round(rightFontSize * 1.3);
          totalH = allLines.length * lineH;
          if (totalH <= (bottomBound - topBound) || rightFontSize === minFontSize) break;
          rightFontSize -= 1;
        }

        const startY = topBound + Math.round(((bottomBound - topBound) - totalH) / 2);
        const startX = rightAreaX + 12;
        const spaceW = ctx.measureText(' ').width;

        ctx.fillStyle = opts.textoRightColor || '#374151';
        for (let i = 0; i < allLines.length; i++) {
          const ln = allLines[i];
          let yPos = startY + i * lineH;
          if (ln.words.length === 0) continue;
          if (i < allLines.length - 1 && ln.words.length > 1) {
            const gaps = ln.words.length - 1;
            const extra = (maxRightW - ln.wordsWidth) / gaps;
            let xPos = startX;
            for (let j = 0; j < ln.words.length; j++) {
              ctx.fillText(ln.words[j], xPos, yPos);
              xPos += ctx.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            ctx.fillText(ln.words.join(' '), startX, yPos);
          }
        }

        ctx.restore();
      }

      // footer
      ctx.fillStyle = opts.footerColor || '#6b7280';
      ctx.font = `${opts.footerFontSize ?? 25}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';

      const footerInset = typeof opts.footerInset === 'number' ? opts.footerInset : 8;
      const footerMaxW = Math.max(0, rightAreaW - footerInset - 8);

      const wrapFooter = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      const footerLines = wrapFooter(ctx, footerText, footerMaxW);
      const footerLineH = Math.round((opts.footerLineHeight ?? 1.2) * (opts.footerFontSize ?? 25));
      const footerBlockH = footerLines.length * footerLineH;
      const defaultFooterY = height - padding - footerBlockH - 200;
      let footerY = defaultFooterY;
      footerY = Math.max(padding, Math.min(footerY, height - padding - footerBlockH));
      const footerX = rightAreaX + rightAreaW - footerInset;
      for (let i = 0; i < footerLines.length; i++) {
        ctx.fillText(footerLines[i], footerX, footerY + i * footerLineH);
      }

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_catorce_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});